// scrollToBlockId.js
// Helper function to scroll to a node with a specific blockId and (optionally)
// set a ProseMirror selection for a character range within that block.

import {TextSelection} from 'prosemirror-state';
import {blockRangeToDocPositions} from './blockRangeToDocPositions';

/**
 * Scroll to a node with the given blockId.
 *
 * Optionally, if `options.range` is provided, also set the editor selection to
 * the corresponding text range within that block.
 *
 * @param {import('prosemirror-view').EditorView} view - The ProseMirror editor view
 * @param {string} blockId - The blockId to scroll to
 * @param {{
 *   range?: [number, number],
 *   focus?: boolean,
 *   behavior?: ScrollBehavior,
 *   block?: ScrollLogicalPosition,
 *   inline?: ScrollLogicalPosition,
 * }=} options
 * @returns {boolean} - True if the block element was found and scrolled to, false otherwise
 */
export function scrollToBlockId(view, blockId, options) {
    if (!view || !blockId) {
        console.warn('scrollToBlockId: Invalid view or blockId', {view, blockId});
        return false;
    }

    const editorElement = view.dom;
    const targetElement = editorElement.querySelector(`[data-block-id="${blockId}"]`);

    if (!targetElement) {
        console.warn(`scrollToBlockId: No element found with data-block-id="${blockId}"`);
        return false;
    }

    try {
        // Scroll into view
        targetElement.scrollIntoView({
            behavior: options?.behavior ?? 'smooth',
            block: options?.block ?? 'center',
            inline: options?.inline ?? 'nearest'
        });

        // Optionally set selection to a range within this block.
        if (options?.range && Array.isArray(options.range) && options.range.length === 2) {
            const [start, end] = options.range;

            const docRange = blockRangeToDocPositions(view, blockId, start, end);
            if (!docRange) {
                console.warn('scrollToBlockId: could not map block range to doc positions', {blockId, start, end});
            } else {
                const {state, dispatch} = view;
                const tr = state.tr.setSelection(TextSelection.create(state.doc, docRange.from, docRange.to));
                dispatch(tr);
            }
        }

        if (options?.focus !== false) {
            view.focus();
        }

        return true;
    } catch (error) {
        console.error('scrollToBlockId: Error scrolling/selecting', error);
        return false;
    }
}
